import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Plus } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { puoFare } from '../utils/permessi';
import api from '../api/client';
import { DialogoHost } from './Dialogo';
import { RigaEvento, FormEvento } from '../pages/calendario/CalendarioAttivita';

// Riquadro Dashboard del Calendario attività (25/10/2026): stessi eventi della pagina /calendario,
// colore della sede, eventi su più giorni in ogni loro giorno. Aggiungi/modifica con la stessa finestra.
const MESI = ['Gennaio','Febbraio','Marzo','Aprile','Maggio','Giugno','Luglio','Agosto','Settembre','Ottobre','Novembre','Dicembre'];
const GIORNI = ['Lun','Mar','Mer','Gio','Ven','Sab','Dom'];
const toISODate = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
const oggiISO = toISODate(new Date());
const colore = (e) => e.colore || e.luogo?.colore || '#64748B';

export default function CalendarioImpegni() {
  const { user } = useAuth();
  const now = new Date();
  const [anno, setAnno] = useState(now.getFullYear());
  const [mese, setMese] = useState(now.getMonth()); // 0-11
  const [eventi, setEventi] = useState([]);
  const [luoghi, setLuoghi] = useState([]);
  const [tags, setTags] = useState([]);
  const [giornoSel, setGiornoSel] = useState(oggiISO);
  const [form, setForm] = useState(null);

  // Griglia mese: da lunedì della prima settimana, 6 settimane
  const primoDelMese = new Date(anno, mese, 1);
  const inizioGriglia = new Date(anno, mese, 1 - (primoDelMese.getDay() + 6) % 7);
  const celle = Array.from({ length: 42 }, (_, i) => { const d = new Date(inizioGriglia); d.setDate(inizioGriglia.getDate() + i); return d; });
  const da = toISODate(celle[0]), a = toISODate(celle[41]);

  const load = useCallback(async () => {
    const r = await api.get(`/calendario?da=${da}&a=${a}`);
    setEventi(r.data);
  }, [da, a]);
  useEffect(() => { load().catch(() => {}); }, [load]);
  const apri = (f) => {
    Promise.all([api.get('/calendario/luoghi'), api.get('/calendario/tags')])
      .then(([l, t]) => { setLuoghi(l.data.filter(x => x.attivo || x.id === f.evento?.luogo?.id)); setTags(t.data); setForm(f); })
      .catch(() => setForm(f));
  };

  const cambiaMese = (delta) => {
    let m = mese + delta, an = anno;
    if (m < 0) { m = 11; an--; } else if (m > 11) { m = 0; an++; }
    setMese(m); setAnno(an);
  };
  const eventiPerGiorno = (g) => eventi
    .filter(e => e.data.slice(0, 10) <= g && (e.data_fine || e.data).slice(0, 10) >= g)
    .sort((x, y) => (x.ora_inizio || '').localeCompare(y.ora_inizio || ''));
  const eventiGiorno = eventiPerGiorno(giornoSel);

  return (
    <div style={s.card}>
      <DialogoHost />
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'12px', gap:8, flexWrap:'wrap' }}>
        <Link to="/calendario" style={{ ...s.cardTitle, textDecoration:'none' }}>CALENDARIO ATTIVITÀ ›</Link>
        <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
          <button style={s.navBtn} onClick={() => cambiaMese(-1)}><ChevronLeft size={14}/></button>
          <span style={{ fontSize:'var(--font-size-sm)', fontWeight:'700', minWidth:'110px', textAlign:'center' }}>{MESI[mese]} {anno}</span>
          <button style={s.navBtn} onClick={() => cambiaMese(1)}><ChevronRight size={14}/></button>
        </div>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:'3px', marginBottom:'4px' }}>
        {GIORNI.map(g => <div key={g} style={s.giornoLabel}>{g}</div>)}
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:'3px', marginBottom:'14px' }}>
        {celle.map(d => {
          const iso = toISODate(d);
          const fuoriMese = d.getMonth() !== mese;
          const selezionato = iso === giornoSel;
          return (
            <div key={iso} onClick={() => setGiornoSel(iso)}
              style={{ ...s.cella, opacity: fuoriMese ? 0.35 : 1, border: selezionato ? '1px solid #F4600C' : '1px solid transparent', background: iso === oggiISO ? '#F4600C10' : 'transparent' }}>
              <span style={{ fontSize:'var(--font-size-xs)', fontWeight: iso === oggiISO ? '800' : '500', color: iso === oggiISO ? '#F4600C' : 'var(--text-muted,#888)' }}>{d.getDate()}</span>
              <div style={{ display:'flex', flexWrap:'wrap', gap:'2px', marginTop:'2px' }}>
                {eventiPerGiorno(iso).slice(0, 4).map(e => (
                  <span key={e.id} title={e.titolo} style={{ width:'6px', height:'6px', borderRadius:'50%', background: colore(e), flexShrink:0 }}/>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <div style={s.divider}/>

      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', margin:'12px 0 10px' }}>
        <div style={{ fontSize:'var(--font-size-sm)', fontWeight:'700', textTransform:'capitalize' }}>
          {new Date(giornoSel + 'T12:00:00').toLocaleDateString('it-IT', { weekday:'long', day:'numeric', month:'long' })}
        </div>
        {puoFare(user, 'calendario', 'create') && <button style={s.btnSec} onClick={() => apri({ data: giornoSel })}><Plus size={12}/> Aggiungi</button>}
      </div>

      <div style={{ display:'flex', flexDirection:'column', gap:'6px' }}>
        {eventiGiorno.length === 0 && <div style={{ color:'var(--text-dim)', fontSize:'var(--font-size-xs)' }}>Nessun evento in questo giorno.</div>}
        {eventiGiorno.map(e => <RigaEvento key={e.id} ev={e} onApri={() => apri({ evento: e })} />)}
      </div>
      {form && <FormEvento {...form} luoghi={luoghi} tags={tags} onChiudi={() => setForm(null)} onSalvato={() => { setForm(null); load(); }} />}
    </div>
  );
}

const s = {
  card: { background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:'12px', padding:'20px' },
  cardTitle: { fontSize:'var(--font-size-xs)', fontWeight:'800', letterSpacing:'2px', color:'var(--text-dim)' },
  navBtn: { background:'var(--bg-input)', border:'1px solid var(--border2)', borderRadius:'6px', padding:'5px 7px', color:'var(--text-muted)', cursor:'pointer', display:'flex' },
  giornoLabel: { textAlign:'center', fontSize:'var(--font-size-xs)', color:'#555', fontWeight:'700' },
  cella: { minHeight:'40px', borderRadius:'6px', padding:'4px', cursor:'pointer', display:'flex', flexDirection:'column' },
  divider: { height:'1px', background:'var(--bg-input)' },
  btnSec: { display:'flex', alignItems:'center', gap:'5px', background:'var(--border)', color:'var(--text)', border:'1px solid var(--border2)', borderRadius:'6px', padding:'5px 10px', fontSize:'var(--font-size-xs)', fontWeight:'700', cursor:'pointer' },
};
